// src/components/Navbar.js
// POS88 v2.0.1 - Navbar พร้อมระบบเช็ก role, logout, backend version, และ API status

import React, { useContext, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import AuthContext from "../AuthContext";
import api from "../api";

const APP_VERSION = "2.0.1";

const Navbar = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [backendVersion, setBackendVersion] = useState("");
  const [apiStatus, setApiStatus] = useState("checking");
  const [mobileOpen, setMobileOpen] = useState(false);

  // ปิดเมนูมือถืออัตโนมัติเมื่อเปลี่ยนหน้า
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // ปิดเมนูเมื่อกดปุ่ม Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const isAdmin = user?.role === "admin";
  const isManager = user?.role === "manager";

  // ดึงข้อมูลเวอร์ชัน backend + สถานะ API
  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const res = await api.get("/version");
        if (res.data?.version) {
          setBackendVersion(res.data.version);
          setApiStatus("online");
        } else {
          setApiStatus("unknown");
        }
      } catch (err) {
        console.error("Backend unavailable:", err);
        setApiStatus("offline");
      }
    };

    fetchVersion();
  }, []);

  // ซ่อน Navbar ในหน้า Login
  if (location.pathname === "/login") {
    return null;
  }

  const renderApiStatus = () => {
    if (apiStatus === "checking")
      return <span className="text-warning small">Checking...</span>;
    if (apiStatus === "online")
      return <span className="text-success small">● Online</span>;
    if (apiStatus === "offline")
      return <span className="text-danger small">● Offline</span>;
    return <span className="text-muted small">-</span>;
  };

  // Helper to check permission
  const hasPermission = (permId) => {
    if (!user) return false;
    if (isAdmin) return true; // Admin always has access
    return user.permissions?.includes(permId);
  };

  // Check if user has ANY management permission to show the header
  const hasAnyManagementPermission = () => {
    if (!user) return false;
    if (isAdmin) return true;
    const managementPerms = [
      "dashboard", "deductions", "commissions", "expenses",
      "profit", "cashflow", "discount_codes", "reports",
      "employees", "settings"
    ];
    return managementPerms.some(p => user.permissions?.includes(p));
  };

  return (
    <>
      {/* Mobile Topbar (< 992px) */}
      <header className="mobile-topbar d-lg-none">
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn text-white p-1 border-0 mobile-nav-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "ปิดเมนู" : "เปิดเมนู"}
            aria-expanded={mobileOpen}
          >
            <i className={`bi ${mobileOpen ? "bi-x-lg" : "bi-list"} fs-4`}></i>
          </button>
          <div
            className="fw-bold text-white fs-5"
            style={{ cursor: "pointer", letterSpacing: "0.5px" }}
            onClick={() => navigate(user ? "/pos" : "/login")}
          >
            <span style={{ color: "var(--ks-accent, #d4af37)" }}>POS</span>88
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
          {renderApiStatus()}
          {user && (
            <span className={`badge ${isAdmin ? "bg-danger" : isManager ? "bg-warning text-dark" : "bg-secondary"}`}>
              {user.display_name || user.username}
            </span>
          )}
        </div>
      </header>

      {/* Backdrop overlay for mobile drawer */}
      {mobileOpen && (
        <div
          className="sidebar-backdrop d-lg-none"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside className={`sidebar ${mobileOpen ? "open" : ""}`} aria-label="แถบเมนูหลัก">
        <div className="sidebar-header d-flex align-items-center justify-content-between px-3 py-2">
          <span
            className="fw-bold"
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigate(user ? "/pos" : "/login");
              setMobileOpen(false);
            }}
          >
            POS88
          </span>
          <div className="d-flex align-items-center gap-2">
            <small className="text-white-50">v{APP_VERSION}{backendVersion && ` / API ${backendVersion}`}</small>
            <button
              type="button"
              className="btn btn-sm text-white-50 d-lg-none p-1 border-0"
              onClick={() => setMobileOpen(false)}
              aria-label="ปิดเมนู"
            >
              <i className="bi bi-x-lg fs-5"></i>
            </button>
          </div>
        </div>

      {/* Account section at top */}
      {user ? (
        <div className="px-3 py-2 border-bottom border-secondary small">
          <div className="d-flex flex-column">
            <strong>{user.display_name || user.username}</strong>{" "}
            <span className={`badge ${isAdmin ? "bg-danger" : isManager ? "bg-warning text-dark" : "bg-secondary"}`}>
              {isAdmin ? "Admin" : isManager ? "Manager" : "Employee"}
            </span>
          </div>
          <div className="d-flex flex-column gap-1">
            <NavLink to="/change-password" className={({ isActive }) => "nav-link text-light p-0" + (isActive ? " active" : "")}>
              <i className="bi bi-key me-1"></i>เปลี่ยนรหัสผ่าน
            </NavLink>
            <button className="btn btn-sm btn-outline-danger" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right me-1"></i>Logout
            </button>
          </div>
        </div>
      ) : (
        <div className="px-3 py-2 border-bottom border-secondary">
          <NavLink to="/login" className={({ isActive }) => "nav-link text-light p-0" + (isActive ? " active" : "")}>
            <i className="bi bi-box-arrow-in-right me-1"></i>Login
          </NavLink>
        </div>
      )}

      <div className="px-3 mb-2 mt-2">{renderApiStatus()}</div>

      <nav
        className="nav flex-column flex-nowrap px-2"
        style={{ overflowY: "auto", overflowX: "hidden", flex: 1 }}
        onClick={(e) => {
          if (e.target.closest("a") || e.target.closest("button")) {
            setMobileOpen(false);
          }
        }}
      >
        {user ? (
          <>
            {/* Core Features */}
            {hasPermission("pos") && (
              <NavLink to="/pos" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                <i className="bi bi-cart me-2"></i>ขาย-บริการ
              </NavLink>
            )}
            {hasPermission("appointments") && (
              <NavLink to="/appointments" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                <i className="bi bi-calendar-check me-2"></i>นัดหมาย
              </NavLink>
            )}
            {hasPermission("customers") && (
              <>
                <NavLink to="/customers" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                  <i className="bi bi-people-fill me-2"></i>VIP Member
                </NavLink>
                <NavLink to="/deposits" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                  <i className="bi bi-wallet-fill me-2"></i>จัดการมัดจำ
                </NavLink>
              </>
            )}
            {hasPermission("services") && (
              <NavLink to="/services" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                <i className="bi bi-box-seam me-2"></i>คลังบริการ-สินค้า
              </NavLink>
            )}
            {/* Barcode Printing Link - Accessible to POS users */}
            {hasPermission("pos") && (
              <NavLink to="/barcode" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                <i className="bi bi-upc-scan me-2"></i>พิมพ์บาร์โค้ด
              </NavLink>
            )}


            {/* Management Section */}
            {hasAnyManagementPermission() && (
              <>
                <div className="text-white-50 small mt-2 px-2">Management</div>

                {hasPermission("dashboard") && (
                  <NavLink to="/dashboard" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-speedometer2 me-2"></i>ภาพรวม
                  </NavLink>
                )}

                {hasPermission("deductions") && (
                  <NavLink to="/deductions" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-dash-circle me-2"></i>หักเงิน
                  </NavLink>
                )}

                {hasPermission("commissions") && (
                  <NavLink to="/commissions" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-percent me-2"></i>คอมมิชชั่น
                  </NavLink>
                )}

                {hasPermission("expenses") && (
                  <NavLink to="/expenses" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-wallet2 me-2"></i>รายจ่ายร้าน
                  </NavLink>
                )}

                {hasPermission("profit") && (
                  <NavLink to="/profit" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-graph-up me-2"></i>กำไรสุทธิ
                  </NavLink>
                )}

                {hasPermission("cashflow") && (
                  <NavLink to="/cashflow" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-arrow-left-right me-2"></i>จัดการเงิน (สด-บัญชี)
                  </NavLink>
                )}

                {hasPermission("discount_codes") && (
                  <NavLink to="/discount-codes" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-tag me-2"></i>โค้ดส่วนลด
                  </NavLink>
                )}

                {hasPermission("reports") && (
                  <NavLink to="/reports" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-file-earmark-text me-2"></i>รายงานสรุปเงินเดือน
                  </NavLink>
                )}

                {hasPermission("employees") && (
                  <NavLink to="/employees" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-people me-2"></i>จัดการพนักงาน
                  </NavLink>
                )}

                {hasPermission("settings") && (
                  <NavLink to="/settings" className={({ isActive }) => "nav-link text-light" + (isActive ? " active" : "")}>
                    <i className="bi bi-gear me-2"></i>ตั้งค่าร้าน
                  </NavLink>
                )}
              </>
            )}
          </>
        ) : (
          <div className="text-center text-white-50 mt-5">
            <small>กรุณาเข้าสู่ระบบ</small>
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className="text-center py-2 text-white-50" style={{ fontSize: "0.7rem", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <div>Dev by Knotthailand</div>

      </div>
    </aside>
    </>
  );
};

export default Navbar;
